import { useAuth } from "@clerk/react";
import { ClientOnly } from "@tanstack/react-router";
import { type LucideIcon, PlusIcon } from "lucide-react";
import type { ReactNode } from "react";

import { AuthnFormDialog } from "@/web/auth/AuthnFormDialog";
import { Button, type ButtonProps, buttonVariants } from "@/web/components/primitives/button";
import { cn } from "@/web/lib/styling";
import { NominateDialog } from "@/web/routes/-nomination/NominateDialog";

type NominateButtonProps = {
  className?: string;
  /** Visible button text. Defaults to the compact "Nominate". */
  label?: string;
  /** Leading icon. Defaults to a plus. */
  icon?: LucideIcon;
  size?: ButtonProps["size"];
  color?: ButtonProps["color"];
  variant?: ButtonProps["variant"];
  /** Replaces the default icon + label trigger contents (e.g. a homepage path tile). */
  children?: ReactNode;
  /** Forwarded to the trigger `<button>` so e2e helpers can target a specific instance. */
  "data-testid"?: string;
};

/**
 * Placeholder while Clerk resolves (and during SSR). A caller-supplied
 * `children` trigger (e.g. a homepage path tile) is rendered inert in the
 * trigger's own styling, so its copy is in the SSR HTML and nothing shifts on
 * hydration; the default compact trigger gets a grey block.
 */
function NominateButtonSkeleton({
  className,
  children,
  size = "sm",
  color = "darkBlue",
  variant = "solid",
}: NominateButtonProps) {
  if (children == null) {
    return <div className="h-10 w-32 shrink-0 bg-muted" aria-hidden />;
  }
  const triggerClassName = cn(
    buttonVariants({ variant, size, color }),
    "font-withus-sans rounded-none",
    className,
  );
  return (
    <span className={cn("inline-flex", /\bw-full\b/.test(triggerClassName) && "w-full")}>
      <div className={triggerClassName}>
        <span className="inline-flex items-center gap-2">{children}</span>
      </div>
    </span>
  );
}

/**
 * CTA for nominating a business. Renders the same visual trigger either way;
 * auth state decides which dialog wraps it. Showing nothing while Clerk is still
 * resolving avoids a flash where an unauthed user briefly sees the "wrong"
 * dialog open over a sign-in screen. The label/icon/size/color props let callers
 * (e.g. the landing-page CTA) restyle the trigger without forking the flow.
 *
 * Wrapped in `<ClientOnly>` so the `useAuth` call below never fires server-side
 * — `<ClerkProvider>` lives under the root's `<ClientOnly>` (see __root.tsx), so
 * any SSR'd route rendering this directly would otherwise throw "useAuth can
 * only be used within the <ClerkProvider />".
 */
export function NominateButton(props: NominateButtonProps) {
  return (
    <ClientOnly fallback={<NominateButtonSkeleton {...props} />}>
      <NominateButtonInner {...props} />
    </ClientOnly>
  );
}

function NominateButtonInner({
  className,
  label = "Nominate",
  icon: Icon = PlusIcon,
  size = "sm",
  color = "darkBlue",
  variant = "solid",
  children,
  "data-testid": testId,
}: NominateButtonProps) {
  const { isLoaded, isSignedIn } = useAuth();

  if (!isLoaded) {
    return (
      <NominateButtonSkeleton className={className} size={size} color={color} variant={variant}>
        {children}
      </NominateButtonSkeleton>
    );
  }

  const trigger = (
    <Button
      variant={variant}
      color={color}
      size={size}
      className={`font-withus-sans rounded-none ${className ?? ""}`}
      data-testid={testId}
    >
      {children ?? (
        <>
          <Icon className="size-4" />
          {label}
        </>
      )}
    </Button>
  );

  if (!isSignedIn) {
    return (
      <AuthnFormDialog
        trigger={trigger}
        title="Please sign in"
        description="Continue with Google or email to nominate a business"
      />
    );
  }

  return <NominateDialog trigger={trigger} />;
}
